/* eslint-disable react-hooks/exhaustive-deps */
import {
  FormControl,
  Stack,
  TextField,
  IconButton,
  useTheme,
  Button,
  useMediaQuery,
  Grid,
  Typography
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import SelectDateRange from 'src/components/SelectDateRange';
import { useLocation } from 'react-router';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearchIncludeKeywords
} from 'src/features/advanceSearchEcommerce';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeLabel from '../../components/DateRangeLabel';
import FilterListIcon from '@mui/icons-material/FilterList';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import TooltipCustomize from 'src/components/TooltipCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import SelectAutoRefreshInterval from 'src/components/SelectAutoRefreshInterval';

import { reducerUpdateMaxCountNode } from 'src/features/analyticArguments';
import { reducerUpdatePrun } from 'src/features/networkAnalysist';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import useRefreshObjectList from 'src/hooks/useRefreshObjectList';
import AdvanceSearchEcommerce from 'src/components/AdvanceSearchEcommerce';
import FilterBrand from 'src/components/FilterEcommerce/FilterBrand';
import FilterSubBrand from 'src/components/FilterEcommerce/FilterSubBrand';
import FilterProduct from 'src/components/FilterEcommerce/FilterProduct';
import FilterLabel from 'src/components/FilterEcommerce/FilterLabel';
import FilterSentiment from 'src/components/FilterEcommerce/FilterSentiment';
import FilterRating from 'src/components/FilterEcommerce/FilterRating';
import { Clear } from '@mui/icons-material';

const limitations = [1, 3, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

interface IProps {
  onRefresh?: () => void;
}

function BottomHeaderEcommerce({ onRefresh }: IProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const title = pathname.split('/')[2];

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const advanceSearch = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    selectedBrand,
    selectedSubBrand,
    selectedLabel,
    ...rest
  } = advanceSearch;
  const query = changeObjectToQuery(rest);
  const [localSearch, setLocalSearch] = useState<string>(query);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
  };

  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setLocalSearch(query);
  }, [advanceSearch]);

  const refreshObjectList = useRefreshObjectList();

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      refreshObjectList();
      if (title === 'sna') dispatch(reducerUpdatePrun(prunLocal));
    }
  };

  // auto refresh metric
  const { selectedAutoRefreshInterval } = useAppSelector(
    (store) => store.autoRefreshMetric
  );
  useEffect(() => {
    if (selectedAutoRefreshInterval === 0) return;
    const interval = setInterval(() => {
      handleRefresh();
    }, selectedAutoRefreshInterval);

    return () => clearInterval(interval);
  }, [selectedAutoRefreshInterval]);

  const { prun } = useAppSelector((state) => state.storeNetworkAnalysist);
  const [prunLocal, setPrunLocal] = useState<number>(prun);

  const handleEnterPrunNumber = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      dispatch(reducerUpdatePrun(prunLocal));
    }
  };

  const {
    isOnTour,
    currentTour: { stepNumber, menu, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep12 =
    isOnTour &&
    stepNumber === 12 &&
    menu === 'social-media' &&
    submenu === 'overview';

  const isCurrentlyOnTourStep14 =
    isOnTour &&
    stepNumber === 14 &&
    menu === 'social-media' &&
    submenu === 'overview';

  return (
    <Stack direction="column" my={2}>
      <Stack
        direction={isLargeScreen ? 'row' : 'column'}
        justifyContent={isLargeScreen ? 'space-between' : 'unset'}
        gap={isLargeScreen ? 3 : 2}
      >
        <ProductTourTooltip
          open={isCurrentlyOnTourStep12}
          placement="bottom"
          onClick={handleClickOpen}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              size="small"
              sx={{ width: { xs: '100%', sm: 590 } }}
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchTwoToneIcon />
                  </IconButton>
                ),
                endAdornment: localSearch ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : null
              }}
              placeholder="Search stream"
              onChange={(e) => setLocalSearch(e.target.value)}
              value={localSearch}
              onKeyDown={handleKeyDown}
            />
            <TooltipCustomize title="Advance search" placement="top-start">
              <Button
                variant="contained"
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.dark
                }}
                onClick={() => handleClickOpen()}
              >
                <FilterListIcon htmlColor={theme.colors.primary.lighter} />
              </Button>
            </TooltipCustomize>
            {open && (
              <AdvanceSearchEcommerce open={open} onClose={handleClose} />
            )}
            {localSearch ? (
              <Button
                variant="outlined"
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6)
                }}
                onClick={handleClear}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Clear Filter</Typography>
                  <Clear />
                </Stack>
              </Button>
            ) : null}
          </Stack>
        </ProductTourTooltip>
        <Stack
          direction="row"
          justifyContent={isLargeScreen ? 'unset' : 'space-between'}
          spacing={2}
          className="timeframeAndRefresh"
        >
          {title === 'sna' ? (
            <TooltipCustomize title="Pruning keeps the relevant part of the data. Enter a percentage from 0 to 100 to choose how much data you want to keep. Decimals allowed (e.g., 0.05)">
              <TextField
                id="prun-number"
                value={prunLocal}
                type="number"
                onChange={(e) => setPrunLocal(parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ width: '80px' }}
                placeholder="0.3"
                onKeyPress={handleEnterPrunNumber}
              />
            </TooltipCustomize>
          ) : null}
          <ProductTourTooltip open={isCurrentlyOnTourStep14}>
            <FormControl
              size="small"
              sx={{
                width: 135,
                ...(isCurrentlyOnTourStep14
                  ? {
                      position: 'relative',
                      zIndex: 1000
                    }
                  : {})
              }}
            >
              <SelectDateRange />
            </FormControl>
          </ProductTourTooltip>
          <Stack direction="row" gap={1}>
            {title !== 'clustering' && (
              <TooltipCustomize
                title="Refresh all metric"
                placement="top-start"
              >
                <Button
                  variant="contained"
                  onClick={handleRefresh}
                  sx={{
                    borderRadius: '6px',
                    height: theme.spacing(5),
                    minWidth: theme.spacing(6),
                    background: theme.palette.primary.main
                  }}
                >
                  <RefreshIcon htmlColor={theme.colors.primary.lighter} />
                </Button>
              </TooltipCustomize>
            )}
            <FormControl size="small" sx={{ width: 100 }}>
              <SelectAutoRefreshInterval />
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
      <Grid container mt={2} spacing={1}>
        <Grid item lg={2}>
          <FilterBrand />
        </Grid>
        <Grid item lg={2}>
          <FilterSubBrand />
        </Grid>
        <Grid item lg={2}>
          <FilterProduct />
        </Grid>
        <Grid item lg={2}>
          <FilterLabel />
        </Grid>
        <Grid item lg={2}>
          <FilterSentiment />
        </Grid>
        <Grid item lg={2}>
          <FilterRating />
        </Grid>
      </Grid>
      <DateRangeLabel label={`Viewing ${title} within`} />
    </Stack>
  );
}

export default BottomHeaderEcommerce;
